import { RoomType, spLogTags } from '@eventr/common'
import { bs } from '../../../state/bound.store'
import { getRocketChatUrl } from '../../../utils'
import { chatStore } from '../chatStore'
import { getChatTypeFromSectionId } from '../rocketChatProvider'
import EmbeddedChatApi from './EmbeddedChatApi'

export default class ChatScreenApi {
    chatApi: EmbeddedChatApi
    rid: string
    roomType: RoomType
    groupType: string
    isSubscribed: boolean
    retryTimeoutId: NodeJS.Timeout | null
    constructor(
        chatApi: EmbeddedChatApi,
        rid: string,
        roomType: RoomType,
        groupType: number
    ) {
        this.chatApi = chatApi
        this.rid = rid
        this.roomType = roomType
        this.groupType = getChatTypeFromSectionId(groupType)
        this.isSubscribed = false
        this.retryTimeoutId = null
        if (this.chatApi) {
            // Initial subscription attempt
            this.subscribeToRoomWithRetry()

            // Setup network change listener
            this.setupNetworkChangeListener()

            this.chatApi.rcClient.onStreamData(
                'stream-notify-room',
                (data: any) => {
                    if (
                        data &&
                        data.fields &&
                        data.fields.eventName &&
                        data.fields.eventName === `${this.rid}/typing`
                    ) {
                        spLogTags('data', ['rocketchat'])
                    }
                }
            )
        } else {
            spLogTags('Error chatApi is undefined', ['rocketchat', 'ERROR'])
        }
    }

    // Clean up resources when instance is destroyed (add to class)
    public dispose() {
        // Clear any pending retry timeouts
        if (this.retryTimeoutId) {
            clearTimeout(this.retryTimeoutId)
            this.retryTimeoutId = null
        }

        // Remove event listeners
        window.removeEventListener('online', this.handleNetworkReconnection)
    }

    // Network reconnection handler
    private handleNetworkReconnection = () => {
        if (!this.isSubscribed && this.chatApi) {
            spLogTags(
                'Network reconnected, attempting to resubscribe to room',
                ['rocketchat']
            )
            this.subscribeToRoomWithRetry()
        }
    }

    private setupNetworkChangeListener() {
        window.addEventListener('online', this.handleNetworkReconnection)
    }

    private subscribeToRoomWithRetry(retryCount = 0, maxRetries = 10) {
        // Check if we have the necessary prerequisites
        if (!this.chatApi || !this.chatApi.rcClient) {
            spLogTags('Cannot subscribe - chat API not initialized', [
                'ERROR',
                'rocketchat',
            ])
            return
        }

        // Clear any existing retry timeout
        if (this.retryTimeoutId) {
            clearTimeout(this.retryTimeoutId)
            this.retryTimeoutId = null
        }

        // Try to subscribe
        this.chatApi.rcClient
            .subscribeRoom(this.rid)
            .then(() => {
                // Update subscription state
                this.isSubscribed = true

                // Log success if we had to retry
                if (retryCount > 0) {
                    spLogTags(
                        `Successfully subscribed to room after ${retryCount} retries`,
                        ['rocketchat']
                    )
                }
            })
            .catch((error) => {
                // Update subscription state
                this.isSubscribed = false

                // Log the error
                spLogTags(error, ['ERROR', 'rocketchat', 'ddp'])

                // Check for unrecoverable errors
                const isUnrecoverable =
                    error &&
                    (error.error === 'not-authorized' ||
                        error.error === 'invalid-user')

                if (isUnrecoverable) {
                    spLogTags(
                        'Unrecoverable subscription error, stopped retrying',
                        ['ERROR', 'rocketchat']
                    )
                    return
                }

                // Determine if we should retry
                const shouldRetry = maxRetries <= 0 || retryCount < maxRetries

                if (shouldRetry) {
                    // Calculate next retry delay (2s fixed delay)
                    const retryDelay = 2000

                    spLogTags(
                        `Retrying subscribing to room in 2s (attempt ${
                            retryCount + 1
                        })`,
                        ['rocketchat']
                    )

                    // Schedule next retry
                    this.retryTimeoutId = setTimeout(() => {
                        this.subscribeToRoomWithRetry(
                            retryCount + 1,
                            maxRetries
                        )
                    }, retryDelay)
                } else {
                    spLogTags(
                        `Failed to subscribe to room after ${maxRetries} attempts`,
                        ['ERROR', 'rocketchat']
                    )
                }
            })
    }
    /**
     * @param {boolean} anonymousMode
     * @param {Object} options This object should include query or fields.
     * query - json object which accepts MongoDB query operators.
     * fields - json object with properties that have either 1 or 0 to include them or exclude them
     * @returns messages
     */
    async getMessages(
        anonymousMode = false,
        options: {
            query?: object | undefined
            field?: object | undefined
            offset?: Number | undefined
            count?: Number | undefined
            sort?: object | undefined
        } = {
            query: undefined,
            field: undefined,
            offset: undefined,
            count: undefined,
            sort: undefined,
        }
    ) {
        return this.chatApi.getMessages(
            this.rid,
            this.roomType,
            anonymousMode,
            options
        )
    }

    async sendTypingStatus(username: string, typing: boolean) {
        if (!this.chatApi.ready) return
        try {
            this.chatApi.rcClient
                .methodCall(
                    'stream-notify-room',
                    `${this.rid}/typing`,
                    username,
                    typing
                )
                .catch((resp) => {
                    spLogTags(resp, ['rocketchat', 'ERROR'])
                })
        } catch (err) {
            spLogTags(err, ['rocketchat', 'ERROR'])
        }
    }

    /**
     * @param {*} message should be a string or an rc message object
     * Refer https://developer.rocket.chat/reference/api/schema-definition/message#message-object
     */
    async sendMessage(message: any, threadId: string, blocks?: any) {
        const messageObj = blocks
            ? typeof message === 'string'
                ? {
                      msg: message,
                      blocks: [blocks],
                      rid: this.rid,
                  }
                : {
                      ...message,
                      blocks: [blocks],
                      rid: this.rid,
                  }
            : typeof message === 'string'
            ? {
                  msg: message,
                  rid: this.rid,
              }
            : {
                  ...message,
                  rid: this.rid,
              }
        if (threadId) {
            messageObj.tmid = threadId
        }
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/chat.sendMessage`,
                {
                    body: JSON.stringify({ message: messageObj }),
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                    method: 'POST',
                }
            )
            return await response.json()
        } catch (err) {
            console.error(err)
        }
    }

    async deleteMessage(msgId: string) {
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/chat.delete`,
                {
                    body: `{"roomId": "${this.rid}", "msgId": "${msgId}","asUser" : true }`,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                    method: 'POST',
                }
            )
            return await response.json()
        } catch (err) {
            console.error(err)
        }
    }

    async updateMessage(msgId: string, text: string) {
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/chat.update`,
                {
                    body: `{"roomId": "${this.rid}", "msgId": "${msgId}","text" : "${text}" }`,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                    method: 'POST',
                }
            )
            return await response.json()
        } catch (err) {
            console.error(err)
        }
    }

    async starMessage(mid: string) {
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/chat.starMessage`,
                {
                    body: `{"messageId": "${mid}"}`,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                    method: 'POST',
                }
            )
            return await response.json()
        } catch (err) {
            console.error(err)
        }
    }

    async unstarMessage(mid: string) {
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/chat.unStarMessage`,
                {
                    body: `{"messageId": "${mid}"}`,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                    method: 'POST',
                }
            )
            return await response.json()
        } catch (err) {
            console.error(err)
        }
    }

    async getStarredMessages() {
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/chat.getStarredMessages?roomId=${this.rid}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                    method: 'GET',
                }
            )
            return await response.json()
        } catch (err) {
            console.error(err)
        }
    }

    async getPinnedMessages() {
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/chat.getPinnedMessages?roomId=${this.rid}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                    method: 'GET',
                }
            )
            return await response.json()
        } catch (err) {
            console.error(err)
        }
    }

    async pinMessage(mid: string) {
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/chat.pinMessage`,
                {
                    body: `{"messageId": "${mid}"}`,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                    method: 'POST',
                }
            )
            return await response.json()
        } catch (err) {
            return {
                error: err,
            }
        }
    }

    async unpinMessage(mid: string) {
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/chat.unPinMessage`,
                {
                    body: `{"messageId": "${mid}"}`,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                    method: 'POST',
                }
            )
            return await response.json()
        } catch (err) {
            console.error(err)
        }
    }

    async reactToMessage(
        emoji: string,
        messageId: string,
        shouldReact?: string
    ) {
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/chat.react`,
                {
                    body: `{"messageId": "${messageId}", "emoji": "${emoji}"}`,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                    method: 'POST',
                }
            )
            return await response.json()
        } catch (err) {
            console.error(err)
        }
    }

    async reportMessage(messageId: string, description: string) {
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/chat.reportMessage`,
                {
                    body: `{"messageId": "${messageId}", "description": "${description}"}`,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                    method: 'POST',
                }
            )
            return await response.json()
        } catch (err) {
            console.error(err)
        }
    }

    async findOrCreateInvite() {
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/findOrCreateInvite`,
                {
                    method: 'POST',
                    body: JSON.stringify({
                        rid: this.rid,
                        days: 1,
                        maxUses: 10,
                    }),
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                }
            )
            return await response.json()
        } catch (err) {
            console.log(err)
        }
    }

    async sendAttachment(
        file: File,
        fileName: string,
        fileDescription = '',
        threadId = undefined
    ) {
        try {
            const form = new FormData()
            if (threadId) {
                form.append('tmid', threadId)
            }
            form.append('file', file, fileName)
            form.append(
                'description',
                fileDescription.length !== 0 ? fileDescription : ''
            )
            const response = await fetch(
                `${this.chatApi.host}/api/v1/rooms.upload/${this.rid}`,
                {
                    method: 'POST',
                    body: form,
                    headers: {
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                }
            )
                .then((r) => {
                    return { status: 'success', response: r.json() }
                })
                .catch((error) => {
                    return { status: 'error', response: error }
                })
            return response
        } catch (err) {
            console.log(err)
        }
    }
    async getChannelMembers() {
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/channels.members?roomId=${this.rid}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                    method: 'GET',
                }
            )

            const resp = await response.json()
            chatStore.value.set('members', this.chatApi.host + this.rid, resp)
            return resp
        } catch (err) {
            console.error(err)
        }
    }
    async getChannelInfo() {
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/channels.info?roomId=${this.rid}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                    method: 'GET',
                }
            )
            const resp = await response.json()
            chatStore.value.set(
                'channelInfo',
                this.chatApi.host + this.rid,
                resp
            )
            return resp
        } catch (err) {
            console.error(err)
        }
    }

    async getRoomInfo() {
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/rooms.info?roomId=${this.rid}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                    method: 'GET',
                }
            )
            const resp = await response.json()
            chatStore.value.set(
                'channelInfo',
                this.chatApi.host + this.rid,
                resp
            )
            return resp
        } catch (err) {
            console.error(err)
        }
    }

    async getChannelCounters() {
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/channels.counters?roomId=${this.rid}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                    method: 'GET',
                }
            )
            const resp = await response.json()
            chatStore.value.set(
                'channelCounters',
                this.chatApi.host + this.rid,
                resp
            )
            return resp
        } catch (err) {
            console.error(err)
        }
    }

    async getDirectChannelMembers() {
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/im.members?roomId=${this.rid}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                    method: 'GET',
                }
            )
            const resp = await response.json()

            chatStore.value.set('members', this.chatApi.host + this.rid, resp)
            return resp
        } catch (err) {
            console.error(err)
        }
    }

    async getSearchMessages(text: string) {
        try {
            const response = await fetch(
                `${this.chatApi.host}/api/v1/chat.search?roomId=${this.rid}&searchText=${text}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': this.chatApi.authToken,
                        'X-User-Id': this.chatApi.userID,
                    },
                    method: 'GET',
                }
            )
            return await response.json()
        } catch (err) {
            console.error(err)
        }
    }
    async execCommand({
        command,
        params,
    }: {
        command: string
        params: string
    }) {
        const response = await fetch(
            `${this.chatApi.host}/api/v1/commands.run`,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'X-Auth-Token': this.chatApi.authToken,
                    'X-User-Id': this.chatApi.userID,
                },
                method: 'POST',
                body: JSON.stringify({
                    command,
                    params,
                    roomId: this.rid,
                    triggerId: Math.random().toString(32).slice(2, 20),
                }),
            }
        )
        const data = await response.json()
        return data
    }

    getCacheKey(store: string, key?: string) {
        let cacheKey
        if (this.chatApi) {
            cacheKey = this.chatApi.host
        } else {
            cacheKey = getRocketChatUrl(bs().selectedOrg.name)
        }
        switch (store) {
            case 'messages':
            case 'users':
            case 'members':
            case 'channelInfo':
                cacheKey += this.rid
                break
            default:
                break
        }
        if (key) {
            cacheKey += key
        }
        return cacheKey
    }

    async getCache<T>(store: string, key?: string) {
        try {
            return chatStore.value?.get<T>(store, this.getCacheKey(store, key))
        } catch {
            return null
        }
    }
}
